import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { generateChannelKey, encryptChannelKey } from '@clinikchat/shared';
import type { ChannelKeyEpoch, KeyEpochStatus } from '@clinikchat/shared';
import { useCryptoStore } from '../../stores/cryptoStore';
import { formatDateAU } from '../../lib/formatDate';

interface Props {
  channelId: string;
  isAdmin: boolean;
}

interface PendingEpoch {
  id: string;
  channelId: string;
  epoch: number;
  status: KeyEpochStatus;
  createdAt: string;
  rotationReason: string | null;
}

interface MyKeyResponse {
  encryptedChannelKey: string | null;
}

const STATUS_LABELS: Record<KeyEpochStatus, string> = {
  ACTIVE: 'Active',
  DISTRIBUTING: 'Distributing',
  SUPERSEDED: 'Superseded',
};

const STATUS_CLASSES: Record<KeyEpochStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  DISTRIBUTING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  SUPERSEDED: 'bg-warm-gray-100 text-warm-gray-500 dark:bg-white/5 dark:text-warm-gray-400',
};

function StatusBadge({ status }: { status: KeyEpochStatus }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function EpochRow({
  epoch,
  channelId,
  currentUserId,
  onAck,
  acking,
}: {
  epoch: ChannelKeyEpoch;
  channelId: string;
  currentUserId: string | undefined;
  onAck: (epoch: number) => void;
  acking: boolean;
}) {
  const { data: myKey } = useQuery<MyKeyResponse>({
    queryKey: ['channel-epoch-my-key', channelId, epoch.epoch],
    queryFn: async () => {
      const { data } = await api.get<{ success: true; data: MyKeyResponse }>(
        `/api/v1/channels/${channelId}/keys/epochs/${epoch.epoch}/my-key`,
      );
      return data.data;
    },
    enabled: !!currentUserId && epoch.status !== 'SUPERSEDED',
    staleTime: 30_000,
  });

  const hasUnacked = !!myKey && myKey.encryptedChannelKey !== null;

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-warm-gray-50 dark:hover:bg-white/5 transition">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-mono text-warm-gray-700 dark:text-warm-gray-300">
          #{epoch.epoch}
        </span>
        <StatusBadge status={epoch.status} />
        <span className="text-xs text-warm-gray-400 hidden sm:block">
          {formatDateAU(epoch.createdAt)}
        </span>
      </div>
      {hasUnacked && epoch.status !== 'SUPERSEDED' && (
        <button
          onClick={() => onAck(epoch.epoch)}
          disabled={acking}
          className="rounded-btn bg-primary px-2.5 py-1 text-xs text-white hover:bg-primary-hover transition disabled:opacity-50"
        >
          Acknowledge
        </button>
      )}
    </div>
  );
}

export default function KeyRotationPanel({ channelId, isAdmin }: Props) {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    data: epochs,
    isLoading,
    error,
  } = useQuery<ChannelKeyEpoch[]>({
    queryKey: ['channel-epochs', channelId],
    queryFn: async () => {
      const { data } = await api.get<{ success: true; data: ChannelKeyEpoch[] }>(
        `/api/v1/channels/${channelId}/keys/epochs`,
      );
      return data.data;
    },
  });

  const { data: pending } = useQuery<PendingEpoch | null>({
    queryKey: ['channel-key-pending', channelId],
    queryFn: async () => {
      const { data } = await api.get<{ success: true; data: PendingEpoch | null }>(
        `/api/v1/channels/${channelId}/keys/pending`,
      );
      return data.data;
    },
  });

  const needsPolling = pending !== null && pending !== undefined;

  useEffect(() => {
    if (needsPolling) {
      pollingRef.current = setInterval(() => {
        void queryClient.invalidateQueries({ queryKey: ['channel-epochs', channelId] });
        void queryClient.invalidateQueries({ queryKey: ['channel-key-pending', channelId] });
      }, 30_000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [needsPolling, channelId, queryClient]);

  const triggerMutation = useMutation({
    mutationFn: async () => {
      const cachedEpochs = queryClient.getQueryData<ChannelKeyEpoch[]>([
        'channel-epochs',
        channelId,
      ]);
      const maxEpoch =
        cachedEpochs && cachedEpochs.length > 0 ? Math.max(...cachedEpochs.map((e) => e.epoch)) : 0;
      const nextEpoch = maxEpoch + 1;

      const { data: channelData } = await api.get<{
        success: true;
        data: { id: string; members: Array<{ userId: string }> };
      }>(`/api/v1/channels/${channelId}`);
      const memberIds = channelData.data.members.map((m) => m.userId);

      const ck = generateChannelKey();

      const pkResults = await Promise.all(
        memberIds.map((uid) =>
          api
            .get<{ data: { publicKey: string | null } }>(`/api/v1/users/${uid}/public-key`)
            .then((r) => ({ userId: uid, publicKey: r.data.data.publicKey }))
            .catch(() => {
              throw new Error('Failed to fetch public key for a channel member — rotation aborted');
            }),
        ),
      );

      const keys = pkResults
        .filter((r): r is { userId: string; publicKey: string } => r.publicKey !== null)
        .map(({ userId, publicKey }) => ({
          userId,
          encryptedChannelKey: encryptChannelKey(ck, publicKey),
        }));

      if (keys.length < memberIds.length)
        throw new Error('Could not encrypt for all members — rotation aborted');

      await api.post(`/api/v1/channels/${channelId}/keys/epochs/${nextEpoch}`, { keys });
      useCryptoStore.getState().setChannelKey(channelId, ck);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['channel-epochs', channelId] });
      void queryClient.invalidateQueries({ queryKey: ['channel-key-pending', channelId] });
      toast.success('Key rotation triggered');
    },
    onError: () => toast.error('Failed to trigger key rotation'),
  });

  const ackMutation = useMutation({
    mutationFn: async (epoch: number) => {
      await api.post(`/api/v1/channels/${channelId}/keys/epochs/${epoch}/ack`, {});
    },
    onSuccess: (_data, epoch) => {
      void queryClient.invalidateQueries({ queryKey: ['channel-epoch-my-key', channelId, epoch] });
      toast.success('Key acknowledged');
    },
    onError: () => toast.error('Failed to acknowledge key'),
  });

  const currentEpoch = epochs?.[0];

  if (isLoading) {
    return <div className="py-6 text-center text-sm text-warm-gray-400">Loading key epochs...</div>;
  }

  if (error) {
    return (
      <div className="py-6 text-center text-sm text-accent-coral">
        Failed to load key rotation status.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-warm-gray-700 dark:text-warm-gray-300">
            Key Rotation
          </h4>
          {currentEpoch && (
            <p className="mt-0.5 text-xs text-warm-gray-400">
              Current epoch: <span className="font-mono">#{currentEpoch.epoch}</span>
              {' — '}
              <StatusBadge status={currentEpoch.status} />
            </p>
          )}
          {needsPolling && (
            <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
              Rotation pending — distributing keys to members
            </p>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending || needsPolling}
            className="rounded-btn bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary-hover transition disabled:opacity-50"
          >
            {triggerMutation.isPending ? 'Rotating...' : 'Trigger rotation'}
          </button>
        )}
      </div>

      {epochs && epochs.length > 0 ? (
        <div className="divide-y divide-warm-border-light dark:divide-warm-dark-border rounded-lg border border-warm-border-light dark:border-warm-dark-border">
          {epochs.map((epoch) => (
            <EpochRow
              key={epoch.id}
              epoch={epoch}
              channelId={channelId}
              currentUserId={currentUserId}
              onAck={(e) => ackMutation.mutate(e)}
              acking={ackMutation.isPending}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-warm-gray-400">No key epochs found.</p>
      )}
    </div>
  );
}
